import { useAuth0 } from "@auth0/auth0-react";
import styles from "./campaigns.module.css";
import Loader from "../../../components/Loader";
import Footer from "../../../components/Footer";
import { ArrowLeft, Home, X } from "react-feather";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Slide, Snackbar, Typography } from "@mui/material";
import { Helmet } from "react-helmet";
import { useCallback, useEffect, useRef, useState } from "react";
import { TableLoaderSmall } from "../../../components/LoaderC";
import axios from "axios";
import { useHistory } from "react-router-dom";
import SendContact from "./components/SendContact";
import CampaignFilter from "./components/CampaignFilter";
import CampaignCard from "./components/CampaignCard";
import { toast } from 'react-smart-toaster';
import withCustomAuthenticationRequired from "../../../auth/withCustomAuthenticationRequired";
import useDebouncedValue from "../../../hooks/useDebouncedValue";

const CHUNK_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;

const Campaigns = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [loading, setLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(true);
  const [refetching, setRefetching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(false);
  const [allData, setAllData] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [filterData, setFilterData] = useState({ searchKey: "" });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const history = useHistory();
  const [selectedItem, setSelectedItem] = useState(null);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [publishModalOpen, setPublishModalOpen] = useState(false);

  const debouncedSearch = useDebouncedValue(filterData.searchKey, SEARCH_DEBOUNCE_MS);
  const sentinelRef = useRef(null);
  const requestSeq = useRef(0);
  const hasLoadedOnceRef = useRef(false);

  const fetchCampaigns = useCallback(async (currentOffset, replace) => {
    const seq = ++requestSeq.current;

    let activeIndicator = "loadingMore";
    if (replace && !hasLoadedOnceRef.current) {
      activeIndicator = "contentLoading";
      setContentLoading(true);
    } else if (replace) {
      activeIndicator = "refetching";
      setRefetching(true);
    } else {
      setLoadingMore(true);
    }
    setLoadMoreError(false);

    let params = { offset: currentOffset, limit: CHUNK_SIZE };
    if (debouncedSearch.trim() !== "") params.search = debouncedSearch.trim();

    try {
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

      const response = await axios.get(process.env.REACT_APP_USER_API_URL + "v1/campaign/email-templates/paginated", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: params,
      });

      // Ignore responses from requests superseded by newer filters
      if (seq !== requestSeq.current) return;

      const newChunk = response.data?.data || [];
      const meta = response.data?.meta || {};
      const nextOffset = meta?.next_offset ?? (currentOffset + newChunk.length);

      setAllData((prev) => (replace ? newChunk : [...prev, ...newChunk]));
      setOffset(nextOffset);
      setHasMore(meta?.has_more ?? (newChunk.length > 0 && nextOffset < (meta?.total ?? 0)));
      setLoadError(false);
      hasLoadedOnceRef.current = true;
    } catch (error) {
      if (seq !== requestSeq.current) return;
      console.error('[Campaigns] Failed to load campaign list', error);
      toast.error(error?.response?.data?.message || 'Unable to load campaigns. Please try again.');

      if (replace) {
        setLoadError(true);
        setAllData([]);
        setHasMore(false);
      } else {
        setLoadMoreError(true);
      }
    } finally {
      if (seq === requestSeq.current) {
        if (activeIndicator === "contentLoading") setContentLoading(false);
        else if (activeIndicator === "refetching") setRefetching(false);
        else setLoadingMore(false);
      }
    }
  }, [getAccessTokenSilently, debouncedSearch]);

  // Load the first chunk whenever search changes (or after a campaign is copied)
  useEffect(() => {
    fetchCampaigns(0, true);
  }, [fetchCampaigns, reloadKey]);

  // Infinite scroll: load the next chunk when the sentinel enters the viewport
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting) && hasMore && !loadingMore && !contentLoading && !refetching && !loadMoreError) {
        fetchCampaigns(offset, false);
      }
    }, { threshold: 0.1 });

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, contentLoading, refetching, loadMoreError, offset, fetchCampaigns]);

  const navigateTo = (routeName) => {
    history.push(routeName);
  }

  const confirmDelete = async () => {
    setDeleteModalOpen(false);
    setLoading(true);
    const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

    axios.delete(process.env.REACT_APP_USER_API_URL + 'v1/campaign/email-templates/' + selectedItem?.id, {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    }).then(response => {
      setSnackbarOpen(true);
      setSnackbarMsg(response.data.message);
      setLoading(false);

      setAllData(prev => {
        return prev?.filter(i => i?.id !== selectedItem?.id);
      });
      setSelectedItem(null);
    }).catch(error => {
      if (typeof error.response !== 'undefined')
        toast.error(error.response.data.message);
      setLoading(false);
    });
  }

  const changeStatusConfirm = async (data) => {
    setLoading(true);
    setArchiveModalOpen(false);
    setPublishModalOpen(false);
    const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

    axios.put(process.env.REACT_APP_USER_API_URL + 'v1/campaign/email-templates/' + selectedItem?.id, { is_archived: data }, {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    }).then((response) => {
      setLoading(false);

      setSnackbarMsg("Email campaign updated successfully.");
      setSnackbarOpen(true);

      setAllData(prev => {
        return prev?.map((item) => {
          if (item.id === selectedItem?.id) {
            return { ...item, is_archived: data };
          }
          return item;
        })
      });

      setSelectedItem(null);
    }).catch((error) => {
      if (typeof error.response !== "undefined")
        toast.error(error.response.data.message);
      setLoading(false);
    });
  }

  const copyCampign = async (id) => {
    setLoading(true);

    const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

    axios.post(process.env.REACT_APP_USER_API_URL + "v1/campaign/email-templates/copy/" + id, {}, {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    }).then((response) => {
      setLoading(false);

      setSnackbarMsg(response?.data?.message);
      setSnackbarOpen(true);

      // Reload the first chunk so the copy shows up in server order
      setReloadKey((prev) => prev + 1);
    }).catch((error) => {
      if (typeof error.response !== "undefined")
        toast.error(error.response.data.message);
      setLoading(false);
    });
  }

  return (<>
    {loading && <Loader />}
    <Helmet title="Email Template" />

    <div className={`${styles.pageTitleWrap}`}>
      <Typography display="inline" className={`${styles.pageTitle}`}>Manage Campaign</Typography>
      <nav aria-label="breadcrumb">
        <ol className={`${styles.breadcrumb}`}>
          <li className={`${styles.breadcrumbItem}`}><span><Home /></span>Email Campaign</li>
          <li className={`${styles.breadcrumbItem} ${styles.active}`}>Manage Campaign</li>
        </ol>
      </nav>
    </div>

    <div className={`${styles.BodyCon}`}>
      <div className={`${styles.mainCardDiv}`}>
        <div className={`${styles.mainCard}`}>
          {contentLoading && (<div className="Loader TableLoader">
            <TableLoaderSmall />
          </div>)}

          {!contentLoading && (<div className="control-pane">
            <div className={`${styles.Row}`}>

              <CampaignFilter filterData={filterData} setFilterData={setFilterData.bind(this)} />

              {allData.length > 0 && <>
                <div className={refetching ? `${styles.NotesRow} ${styles.Refetching}` : `${styles.NotesRow}`}>
                  {allData.map((et, index) => {
                    return <CampaignCard key={et?.id ?? index} item={et} navigateTo={navigateTo.bind(this)} setSendModalOpen={setSendModalOpen.bind(this)} setSelectedItem={setSelectedItem.bind(this)} setDeleteModalOpen={setDeleteModalOpen.bind(this)} setPublishModalOpen={setPublishModalOpen.bind(this)} setArchiveModalOpen={setArchiveModalOpen.bind(this)} copyCampign={copyCampign.bind(this)} />
                  })}

                  {/* Sentinel: entering the viewport loads the next chunk */}
                  <div ref={sentinelRef} className={styles.ScrollSentinel} />
                </div>

                {loadingMore && (
                  <div className={styles.gridScrollStatus}>
                    <div className={styles.scrollLoaderWrap}>
                      <div className={styles.scrollSpinner} />
                      <span>Loading more campaigns...</span>
                    </div>
                  </div>
                )}

                {loadMoreError && (
                  <div className={styles.gridScrollStatus}>
                    <p>Couldn't load more campaigns.</p>
                    <button type="button" className={styles.RetryBu} onClick={() => fetchCampaigns(offset, false)}>Retry</button>
                  </div>
                )}
              </>}

              {allData.length === 0 && loadError && (<div className={`${styles.NoDataMain}`}>
                <p className={`${styles.NoDataText}`}>Something went wrong</p>
                <p className={`${styles.NoDataTextSub}`}>We couldn't load campaigns. Please try again.</p>
                <button type="button" className={`${styles.RetryBu}`} onClick={() => fetchCampaigns(0, true)}>Retry</button>
              </div>)}

              {allData.length === 0 && !loadError && (<div className={`${styles.NoDataMain}`}>
                <div className={`${styles.NoDataIMG}`}>
                  <img src="/static/img/no-prospect.png" alt="No Data" />
                </div>
                <p className={`${styles.NoDataText}`}>Let's get started!</p>
                <p className={`${styles.NoDataTextSub}`}>You don't have any campaigns saved.</p>
              </div>)}
            </div>
          </div>)}
        </div>
      </div>
    </div>

    <Dialog
      open={deleteModalOpen}
      onClose={(e) => setDeleteModalOpen(false)}
      fullWidth
      maxWidth="sm"
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className="NewPopUp"
    >
      <DialogTitle id="alert-dialog-title">
        Would you like to delete this campaign?
        <button onClick={(e) => setDeleteModalOpen(false)}><X /></button>
      </DialogTitle>
      <DialogContent>
        <div className="ModalFormGroup">
          <label className="PopupBodyText">The campaign information will be removed immediately. You can't undo this action.</label>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={(e) => confirmDelete(false)} className="CancelPopupBU">Delete</Button>
        <Button onClick={(e) => setDeleteModalOpen(false)} className="SubmitPopupBU">Cancel</Button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={archiveModalOpen}
      onClose={(e) => setArchiveModalOpen(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className="NewPopUp"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="alert-dialog-title">Are you sure you want to archive email campaign?<button onClick={(e) => setArchiveModalOpen(false)}><X /></button></DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          <div className="ModalFormGroup">
            <label className="PopupBodyText">The email campaign will be archived immediately. You can publish it at any time.</label>
          </div>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <button onClick={(e) => setArchiveModalOpen(false)} className="CancelPopupBU">Cancel</button>
        <button className="SubmitPopupBU" onClick={(e) => changeStatusConfirm(1)}>Archive</button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={publishModalOpen}
      onClose={(e) => setPublishModalOpen(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className="NewPopUp"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="alert-dialog-title">Are you sure you want to publish email campaign?<button onClick={(e) => setPublishModalOpen(false)}><X /></button></DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          <div className="ModalFormGroup">
            <label className="PopupBodyText">The email campaign will be published immediately. You can unpublish it at any time.</label>
          </div>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <button onClick={(e) => setPublishModalOpen(false)} className="CancelPopupBU">Cancel</button>
        <button className="SubmitPopupBU" onClick={(e) => changeStatusConfirm(0)}>Publish</button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={sendModalOpen}
      onClose={(e) => { setSendModalOpen(false) }}
      fullWidth
      maxWidth="sm"
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className="NewPopUp MobileForm"
    >
      <DialogTitle id="alert-dialog-title">
        <button className={`${styles.BackAddBU}`} onClick={(e) => { setSendModalOpen(false) }}><ArrowLeft /></button>
        Send Email
        <button onClick={(e) => { setSendModalOpen(false) }}><X /></button>
      </DialogTitle>
      <DialogContent>
        <SendContact setAddModalOpen={setSendModalOpen.bind(this)} campaign={selectedItem} setSnackbarOpen={setSnackbarOpen.bind(this)} setSnackbarMsg={setSnackbarMsg.bind(this)} setLoading={setLoading.bind(this)} />
      </DialogContent>
    </Dialog>

    <Snackbar
      open={snackbarOpen}
      onClose={(e) => setSnackbarOpen(false)}
      TransitionComponent={Slide}
      message={snackbarMsg}
      autoHideDuration={10000}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      action={
        <IconButton
          size="small"
          aria-label="close"
          color="inherit"
          onClick={(e) => setSnackbarOpen(false)}
        >
          <X />
        </IconButton>
      }
    />

    <Footer />
  </>);
};

export default withCustomAuthenticationRequired(Campaigns, {
  onRedirecting: () => <Loader />,
});